import { Component, Prop, h, Host, Build } from '@stencil/core';
import { ACTION_REGISTRY, type ActionName } from '../../../../../actions';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-call-to-action-type-6';

/**
 * Dark-navy CTA banner for SignNow forms pages.
 * Structurally identical to `sn-call-to-action-type-5` but uses a dark-navy background.
 * Click behavior, by slotted action:
 *  - a button declaring `trigger-actions` (e.g. `nav:anchor-scroll`) → runs those actions
 *    (parsed here so it works during SSR/hydration, where props arrive as JSON strings);
 *  - a real link (`<a>` with a non-empty `href`) → native navigation;
 *  - otherwise → dispatches the `sn-form-loader-open` custom event to open the form loader.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Call-to-action banner with dark-navy background for encouraging users to create forms
 * @example
 *   <sn-call-to-action-type-6 form-id="my-form-id">
 *     <me-text slot="label" tag="p" class="me-text--label">BE READY TO GET MORE</me-text>
 *     <me-heading slot="heading" variant="2" tag="h2">Create this form in 5 minutes or less</me-heading>
 *     <me-button slot="action-primary" variant="primary" tag="button">Get Form</me-button>
 *   </sn-call-to-action-type-6>
 * @slot label - <me-text tag="p" class="me-text--label"> - Uppercase label above the heading
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main CTA heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Action button (triggers form loader)
 */
@Component({
  tag: 'sn-call-to-action-type-6',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType6 {
  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Unique form identifier passed as detail to the `sn-form-loader-open` event
   */
  @Prop({ attribute: 'form-id', reflect: true }) formId?: string;

  private handleCtaClick = async (event: Event) => {
    if (!Build.isBrowser) return;

    // A slotted button can declare `trigger-actions` / `action-data` (e.g. nav:anchor-scroll).
    // Run them here so it works during SSR/hydration too, where these props arrive as
    // JSON-string attributes rather than parsed arrays/objects.
    const actionEl = (event.target as HTMLElement | null)?.closest('[trigger-actions]') as
      | (HTMLElement & { triggerActions?: unknown; actionData?: unknown })
      | null;

    if (actionEl) {
      event.preventDefault();
      // Attribute first (authoritative after SSR/hydration), then the property
      // (set via JS/property binding, e.g. in Storybook). parseJsonProp handles
      // both the JSON string and an already-parsed array/object.
      const actions = parseJsonProp(actionEl.getAttribute('trigger-actions') ?? actionEl.triggerActions, []) as ActionName[];
      const data = parseJsonProp(actionEl.getAttribute('action-data') ?? actionEl.actionData, {}) as Record<string, unknown>;

      for (const name of actions) {
        const loadAction = ACTION_REGISTRY[name];
        if (!loadAction) continue;
        const { default: Action } = await loadAction();
        await new Action().execute({
          event: event as MouseEvent,
          element: actionEl,
          component: this,
          data: data?.[name] ?? data,
        });
      }
      return;
    }

    // A real link handles its own navigation.
    const href = (event.target as HTMLElement | null)?.closest('a[href]')?.getAttribute('href');
    if (href && href !== '#') {
      return;
    }

    // Otherwise open the form loader.
    event.preventDefault();
    const formId = this.formId || (document.querySelector('[data-formid]') as HTMLElement)?.dataset?.formid;
    window.dispatchEvent(new CustomEvent('sn-form-loader-open', { detail: formId }));
  };

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME}>
          <div class={`${COMPONENT_NAME}__container me-container`}>
            <div class={`${COMPONENT_NAME}__inner`}>
              <slot name="label"></slot>
              <slot name="heading"></slot>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div class={`${COMPONENT_NAME}__action js-editor-link`} onClick={this.handleCtaClick}>
                <slot name="action-primary"></slot>
              </div>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
